// src/pages/AdminPanel.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { resolveProxyWsUrl, withWsToken } from '../utils/proxyWs';

function DevicesPanel() {
    const [socket, setSocket] = useState(null);
    const [devices, setDevices] = useState({});
    const devicesRef = useRef({});

    useEffect(() => {
        // Shared, self-healing resolver — strips stale ws:// overrides on https.
        const wsUrl = resolveProxyWsUrl();

        const s = new WebSocket(withWsToken(wsUrl));

        s.onopen = () => console.log('🟢 Devices WS connected');
        s.onerror = (error) => console.error('Devices WS error:', error);
        s.onclose = () => console.warn('Devices WS closed');

        s.onmessage = (msg) => {
            try {
                const parsed = JSON.parse(msg.data);
                const { type, ip } = parsed;

                if (type === 'new_ip' && ip) {
                    setDevices(prev => {
                        const updated = {
                            ...prev,
                            [ip]: {
                                ...prev[ip],
                                ...parsed,
                                status: parsed.status || prev[ip]?.status || 'allowed',
                                firstSeen: prev[ip]?.firstSeen || parsed.firstSeen || new Date().toLocaleString(),
                            },
                        };
                        devicesRef.current = updated;
                        return updated;
                    });
                }

                if (type === 'allowed_ips' && Array.isArray(parsed.data)) {
                    setDevices(prev => {
                        const updated = { ...prev };
                        parsed.data.forEach(ipAddr => {
                            updated[ipAddr] = {
                                ...updated[ipAddr],
                                status: 'allowed',
                                ipType: updated[ipAddr]?.ipType || 'mobile',
                                firstSeen: updated[ipAddr]?.firstSeen || new Date().toLocaleString(),
                            };
                        });
                        devicesRef.current = updated;
                        return updated;
                    });
                }
            } catch (err) { console.error('❌ Failed to parse Devices WS message', err); }
        };

        setSocket(s);
        return () => s.close();
    }, []);

    // Auto-refresh the filter every 30s to prune old devices even if no new data comes in
    // The value is never read — the state exists only so the interval below
    // can force the 30s re-render that prunes stale devices.
    const [, setTick] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => setTick(t => t + 1), 30000);
        return () => clearInterval(timer);
    }, []);

    const allowIP = (ip) => { if (socket?.readyState === WebSocket.OPEN) { socket.send(JSON.stringify({ type: 'allow', ip })); setDevices(prev => ({ ...prev, [ip]: { ...prev[ip], status: 'allowed' } })); } };
    const blockIP = (ip) => { if (socket?.readyState === WebSocket.OPEN) { socket.send(JSON.stringify({ type: 'block', ip })); setDevices(prev => ({ ...prev, [ip]: { ...prev[ip], status: 'blocked' } })); } };

    const getIcon = (type) => {
        if (!type) return '❓';
        const t = type.toLowerCase();
        if (t.includes('ios')) return '🍎';
        if (t.includes('android')) return '🤖';
        if (t.includes('mac') || t.includes('local')) return '🖥️';
        if (t.includes('windows')) return '💻';
        return '📱';
    };

    const grouped = useMemo(() => {
        const ACTIVE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes
        const now = Date.now();

        const g = { mobile: [], local: [], unknown: [] };

        Object.entries(devices).forEach(([ip, data]) => {
            // If no lastSeen available (legacy or newly added), assume active.
            // Otherwise, check if lastSeen is within the threshold.
            const isDeviceActive = !data.lastSeen || (now - new Date(data.lastSeen).getTime()) < ACTIVE_THRESHOLD_MS;

            if (isDeviceActive) {
                g[data.ipType || 'unknown'].push([ip, data]);
            }
        });
        return g;
    }, [devices]);

    const th = { borderBottom: '1px solid #ccc', textAlign: 'left', padding: '8px 4px' };
    const renderRows = (entries) => entries
        .filter(([, data]) => !data.isBridged) // <--- HIDE DUPLICATES
        .map(([ip, data]) => (
            <tr key={ip} style={{ background: data.status === 'blocked' ? 'var(--error-bg)' : 'var(--card)' }}>
                <td
                    onClick={() => data.status === 'allowed' && (window.location.href = `/?device=${encodeURIComponent(ip)}`)}
                    style={{ cursor: data.status === 'allowed' ? 'pointer' : 'default', color: data.status === 'allowed' ? 'var(--primary)' : 'inherit', fontWeight: '500' }}
                    title={data.status === 'allowed' ? "Click to inspect this device" : ""}
                >
                    {ip}
                </td>
                <td style={{ fontFamily: 'monospace', color: '#007aff', fontWeight: 'bold' }}>
                    {data.ssid || <span style={{ color: '#ccc' }}>-</span>}
                </td>
                <td
                    onClick={() => data.status === 'allowed' && (window.location.href = `/?device=${encodeURIComponent(ip)}`)}
                    style={{ cursor: data.status === 'allowed' ? 'pointer' : 'default' }}
                    title={data.status === 'allowed' ? "Click to inspect this device" : ""}
                >
                    <div style={{ fontWeight: 'bold' }}>{getIcon(data.platformClass)} {data.platformClass || 'Unknown'}</div>
                    <div style={{ fontSize: 11, color: '#666' }}>
                        {data.deviceInfo?.device || 'Unknown Device'} • {data.normalizedPlatform || 'Unknown OS'}
                    </div>
                </td>
                <td>
                    {data.status === 'allowed'
                        ? <span style={{ color: 'green', fontWeight: 'bold' }}>✅ Allowed</span>
                        : <span style={{ color: 'red', fontWeight: 'bold' }}>❌ Blocked</span>}
                </td>
                <td style={{ fontSize: 12, color: '#666' }}>{data.firstSeen}</td>
                <td>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 12 }}>
                        <input type="checkbox" checked={!!data.omidEnabled} onChange={(e) => {
                            if (socket && socket.readyState === WebSocket.OPEN) {
                                socket.send(JSON.stringify({ type: 'toggle_omid', ip, enabled: e.target.checked }));
                                setDevices(prev => ({ ...prev, [ip]: { ...prev[ip], omidEnabled: e.target.checked } }));
                            }
                        }} />
                        Inj.
                    </label>
                </td>
                <td>
                    {data.status === 'allowed'
                        ? <button onClick={() => blockIP(ip)} style={{ padding: '4px 8px', borderColor: 'var(--error-border)', color: 'var(--error-text)', background: 'var(--card)', borderRadius: 4, cursor: 'pointer' }}>Block</button>
                        : <div style={{ display: 'flex', gap: 5 }}>
                            <button onClick={() => allowIP(ip)} style={{ padding: '4px 8px', borderColor: 'var(--success-border)', color: 'var(--success-text)', background: 'var(--card)', borderRadius: 4, cursor: 'pointer' }}>Allow</button>
                            <button onClick={() => {
                                const newSsid = prompt("Enter Session ID to claim this device:", data.ssid || `Session_${Math.random().toString(36).slice(2, 6).toUpperCase()}`);
                                if (newSsid) {
                                    socket.send(JSON.stringify({ type: 'claim_device', ip, ssid: newSsid }));
                                }
                            }} style={{ padding: '4px 8px', borderColor: '#007aff', color: '#007aff', background: 'var(--card)', borderRadius: 4, cursor: 'pointer' }}>Claim</button>
                        </div>}
                </td>
            </tr>
        ));

    return (
        <div>
            <div style={{ marginBottom: 12 }}>
                <a href="/mac-proxy.mobileconfig" download
                    className="btn primary"
                    style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    🖥️ Install Mac Proxy Profile
                </a>
            </div>
            <p>Use this panel to Allow ✅ or Block ❌ devices dynamically. Groups by IP type.</p>

            {['mobile', 'local', 'unknown'].map((type) =>
                grouped[type].length > 0 ? (
                    <div key={type} style={{ marginTop: 24 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <h3>{type === 'mobile' ? '📱 Mobile Devices' : type === 'local' ? '🖥️ Local Devices' : '❓ Unknown Devices'}</h3>
                            {/* OMID Global Toggle per group? Or global? Let's do a global one at the top actually, or per device. User said "when user wants"... implying per-session or global. Let's do Global for now or per device if possible. Plan said "Add UI toggle 'Enable OMID Injection'". I will add a global toggle for now to keep it simple as per plan. */}
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr><th style={th}>IP Address</th><th style={th}>Session ID</th><th style={th}>Type + Device</th><th style={th}>Status</th><th style={th}>First Seen</th><th style={th}>OMID</th><th style={th}>Action</th></tr></thead>
                            <tbody>{renderRows(grouped[type])}</tbody>
                        </table>
                    </div>
                ) : null
            )}
        </div>
    );
}

/* ---------------- Page with tabs ---------------- */
export default function AdminPanel() {
    // HAR History moved to the top-bar Captures page; old /admin#hars links follow it.
    if (typeof window !== 'undefined' && window.location.hash === '#hars') return <Navigate to="/captures" replace />;

    return (
        <div style={{ padding: 20, fontFamily: 'var(--font-sans)' }}>
            <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Link to="/" style={{ textDecoration: 'none', color: 'var(--primary)' }}>Home</Link>
                <div style={{ width: 1, height: 16, background: 'var(--line)' }} />
                <span style={{ color: 'var(--ink)', fontWeight: 600 }}>Admin Panel</span>
            </div>

            <h2 style={{ marginTop: 0 }}>Admin Panel</h2>

            <div style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>
                Saved HARs moved to <Link to="/captures" style={{ color: 'var(--primary)' }}>Captures</Link> in the top bar.
            </div>

            <DevicesPanel />
        </div>
    );
}
